<template>
  <div class="dropdown-box"
    :class="{ 'inline': inline, 'auto-size': autoSize }"
    :title="title"
    ref="mainContainer">
    <div
      id="dropdown-input"
      :class="{ 'focused': isShowDropdown, 'disabled': disabled }"
      @click="inputClick"
      ref="dropDown">
      <slot name="prefix">
        <div v-if="hasPrefix" class="prefix" :style="prefixStyle" v-html="prefix"></div>
      </slot>
      <div v-if="hasPrefix && showSeparator" class="separator-line"></div>
      <div v-if="!hasValue && placeholder" class="dropdown-input-placeholder">{{placeholder}}</div>
      <div v-if="hasValue" class="dropdown-value">{{currentValue}}</div>
      <div v-if="!disabled && !readonly" class="dropdown-arrow-icon"></div>
    </div>
    <div
      class="dropdownbox-container"
      :class="{'has-search-input': showSearchInput}"
      ref="dropdownContainer"
      v-show="isShowDropdown">
      <div class="list-header" ref="listHeader" v-if="showSearchInput">
        <input
          ref="searchInput"
          class="search-input"
          v-model="searchText"
          placeholder="поиск..."
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="selectActive"
          @keydown.esc="closeDropDown"
        />
        <span v-if="!searchText.length" class="search-icon"></span>
        <span v-else class="clear-icon" @click="searchText=''"></span>
      </div>
      <ul ref="dropdownbox"
        @keydown.down.prevent="moveActive(1)"
        @keydown.up.prevent="moveActive(-1)"
        @keyup.esc="closeDropDown"
        class="multiselect-ul"
        :class="{ 'hidden-scroll': hideScrollbar, 'has-search-input': showSearchInput }">
        <li v-if="isMobileView && showPlaceholderOption"
          class="dropitem disabled"
          key="$disabled"
          :id="'dropdown-li-'"
          :class="{ active: !value, 'keyboard-hover': -1 === activeIndex }"
          >
          <slot name="option-icon" v-bind:option="null"></slot>
          <slot name="option-text">
            <div class="droptext">{{placeholder}}</div>
          </slot>
          <slot v-if="showRadio" name="option-checkbox-disabled" v-bind:option="null">
            <input type="radio" class="drop-radio" disabled=true :checked="value == null">
          </slot>
        </li>
        <li v-if="!options || !filteredOptions.length" class="dropitem" id="dropdown-li-empty-search">
          <slot name="option-text">
            <div class="droptext">нет данных</div>
          </slot>
        </li>
        <li v-for="(option, index) in filteredOptions"
          class="dropitem"
          :ref="'item_' + index"
          :key="option[fieldKey]"
          @keydown.enter="onSelectOption(option)"
          :id="'dropdown-li-' + option[fieldKey]"
          :class="{ active: option[fieldKey] == value, 'keyboard-hover': index === activeIndex }"
          @mouseenter="onOptionFocused(option)"
          @click="onSelectOption(option)">
          <slot name="option-icon" v-bind:option="option"></slot>
          <div class="droptext">
            <slot name="option-text">
              <div>{{option[fieldValue]}}</div>
            </slot>
            <slot name="option-subline" v-bind:option="option">
              <div class="subline-text" v-if="fieldSublineKey && fieldSublineKey.length">{{option[fieldSublineKey]}}</div>
            </slot>
          </div>
          <slot v-if="showRadio" name="option-checkbox" v-bind:option="option">
            <input type="radio" class="drop-radio" :checked="option[fieldKey] == value">
          </slot>
        </li>
      </ul>
      <div class="list-footer" @click="closeDropDown">Закрыть</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DropDownBox',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: []
    },
    fieldKey: {
      type: String,
      default: 'key'
    },
    fieldValue: {
      type: String,
      default: 'value'
    },
    fieldSublineKey: {
      type: String,
      default: null
    },
    prefix: {
      type: String,
      default: ''
    },
    noWrapPrefix: {
      type: Boolean,
      default: false
    },
    prefixColor: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number, Boolean],
      default: null,
    },
    showRadio: {
      type: Boolean,
      default: false
    },
    hideScrollbar: {
      type: Boolean,
      default: false
    },
    showSeparator: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showPlaceholderOption: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
    autoSize: {
      type: Boolean,
      default: false
    },
    showSearchInput: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      isMobileView: false,
      isShowDropdown: false,
      selectedOption: null,
      searchText: '',
      activeIndex: -2 // Индекс текущего подсвеченного элемента
    }
  },
  watch: {
    isShowDropdown: function() {
      if (this.isShowDropdown) {
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = 'auto';
      }
    }
  },
  computed: {
    hasPrefix() {
      return this.prefix && this.prefix != '';
    },
    hasValue() {
      return this.fieldKey && this.fieldValue && (this.value || this.value == 0);
    },
    currentValue() {
      let text = '';
      if (this.fieldKey && this.fieldValue && (this.value || this.value == 0) && this.options && this.options.length > 0) {
        const option = this.options.find(p => p[this.fieldKey] == this.value);
        if (option) text = option[this.fieldValue];
      }
      return text;
    },
    prefixStyle() {
      if (this.prefixColor && this.prefixColor != '') {
        return `color: ${this.prefixColor}!important;` + `'white-space': ${this.noWrapPrefix ? 'nowrap' : 'break-spaces'}`;
      }
      else {
        return `color: inherit;` + `white-space: ${this.noWrapPrefix === true ? 'nowrap' : 'break-spaces'}`;
      }
    },
    filteredOptions() {
      return this.getFilteredItems();
    },
    // genUniqId() {
    //   const datestr = new Date().getTime().toString();
    //   const randomstr = Math.random().toString();
    //   // return string + '_' + MD5(datestr + randomstr);
    //   return datestr + randomstr;
    // },
  },
  methods: {
    preventScroll(e) {
      if (this.disabled === true)
        return;
      if (this.$refs.dropdownbox &&
        !this.$refs.dropdownbox.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      return true;
    },
    getFilteredItems() {
      if (!this.searchText || !this.searchText.length) {
        return this.options;
      }
      const lowerCaseInput = this.searchText.toLowerCase();
      if (this.fieldValue && this.fieldValue.length) {
        return this.options.filter(s =>
          s[this.fieldValue].toLowerCase().indexOf(lowerCaseInput) !== -1);
      }

      return this.options;
    },
    inputClick() {
      if (this.disabled === true || this.readonly === true)
        return;
      if (!this.isShowDropdown) {
        this.isShowDropdown = true;
        if (!this.isMobileView) {
          this.$refs.dropdownContainer.style.width = this.$refs.dropDown.getBoundingClientRect().width + 'px';
          window.requestAnimationFrame(() => {
            const input = this.$refs.dropDown;
            const rect = input.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - 5;
            let dropdownHeight = this.$refs.dropdownbox.offsetHeight + 5;
            
            if (this.$refs.listHeader) {
              const listHeaderHeight = this.$refs.listHeader.getBoundingClientRect().height;
              dropdownHeight += listHeaderHeight;
            }
            
            if (spaceBelow < dropdownHeight && rect.top + rect.height > dropdownHeight) {
              // Места снизу мало, а сверху достаточно — открываем вверх
              this.$refs.dropdownContainer.style.top = Math.max(0, (rect.top - dropdownHeight - 5)) + 'px';
              this.$refs.dropdownContainer.style.bottom = 'auto';
            } else {
              // По умолчанию открываем вниз
              this.$refs.dropdownContainer.style.top = this.$refs.dropDown.getBoundingClientRect().bottom + 'px';
              this.$refs.dropdownContainer.style.bottom = 'auto';
            }
          });

          document.querySelector('.container').addEventListener('wheel', this.preventScroll);
        } else {
          this.$refs.dropdownContainer.style.top = 0;
        }
        this.$refs.dropdownbox.setAttribute('tabindex', '0');
        if (this.$refs.searchInput) {
          window.requestAnimationFrame(() => this.$refs.searchInput.focus());
        } else {
          window.requestAnimationFrame(() => this.$refs.dropdownbox.focus());
        }
        this.$emit('focus');
      } else {
        this.closeDropDown();
      }
    },

    // openDropDown() {
    //   this.isShowDropdown = true;
    //   document.addEventListener('mousedown', this.handleOutsideClick);
    //   if (window.innerWidth >= 800) {
    //     this.$refs.dropdownContainer.style.top = this.$refs.dropDown.getBoundingClientRect().bottom + 'px';
    //     this.$refs.dropdownContainer.style.width = this.$refs.dropDown.getBoundingClientRect().width + 'px';
    //   } else {
    //     this.$refs.dropdownContainer.style.top = 0;
    //   }
    //   this.$refs.dropdownbox.setAttribute('tabindex', '0');
    //   // window.requestAnimationFrame(() => this.$refs.searchInput.focus());
    //   this.$emit('focus');
    // },

    closeDropDown() {
      this.isShowDropdown = false;
      document.removeEventListener('mousedown', this.handleOutsideClick);
      var container = document.querySelector('.container');
      if (container) {
        container.removeEventListener('wheel', this.preventScroll);
      }
      this.$emit('blur');
    },

    handleEnterKeyUp(e) {
      if (this.isShowDropdown) return;
      this.inputClick(e);
    },
    onOptionFocused(option) {
      this.selectedOption = option[this.fieldKey];
      this.$emit('option-focus', option);
    },
    onSelectOption(option) {
      this.closeDropDown();
      const value = option ? option[this.fieldKey] : null;
      this.$emit('input', value);
    },
    handleBodyClick(e) {
      if (!(this.$refs.listHeader && this.$refs.listHeader.contains(e.target)) &&
        !(this.$refs.dropdownbox && this.$refs.dropdownbox.contains(e.target)) &&
        !(this.$refs.dropDown && this.$refs.dropDown.contains(e.target))) {
        this.closeDropDown();
      }
    },
    // Навигация стрелками
    moveActive(direction) {
      let minIndex = -2;
      if (!this.options || !this.filteredOptions.length) {
        if (this.showPlaceholderOption) this.activeIndex = -1;
        return;
      } else if (!this.showPlaceholderOption) {
        minIndex = -1;
      }
      
      const newIndex = Math.max(this.activeIndex, minIndex) + direction;
      const ph = this.showPlaceholderOption ? 1 : 0
      if (newIndex >= 0 - ph && newIndex < this.filteredOptions.length) {
        this.activeIndex = newIndex;
        this.scrollToActive();
      }
    },

    // Выбор через Enter
    selectActive() {
      if (this.activeIndex == -1 && this.showPlaceholderOption) {
        this.onSelectOption(null);
      } else if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
        this.onSelectOption(this.options[this.activeIndex]);
      }
    },

    // Автоматический скролл к элементу
    scrollToActive() {
      this.$nextTick(() => {
        const container = this.$refs.dropdownbox;
        if (this.activeIndex == -1 && this.showPlaceholderOption) {
          container.scrollTop = 0;
          return;
        }
        const el = this.$refs['item_' + this.activeIndex]?.[0];
        if (!el) return;
        if (el.offsetTop < container.scrollTop) {
          container.scrollTop = el.offsetTop;
        } else if (el.offsetTop + el.offsetHeight > container.scrollTop + container.offsetHeight) {
          container.scrollTop = el.offsetTop + el.offsetHeight - container.offsetHeight;
        }
      });
    },
    updateViewStyle() {
      this.isMobileView = window.innerWidth < 800;
    },
  },
  mounted() {
    this.updateViewStyle();
    window.addEventListener('mousedown', this.handleBodyClick, true);
    window.addEventListener('resize', this.updateViewStyle, true);
    window.addEventListener('orientationchange', this.updateViewStyle, true);
  },
  beforeDestroy() {
    window.removeEventListener('mousedown', this.handleBodyClick, false);
    window.removeEventListener('resize', this.updateViewStyle, false);
    window.removeEventListener('orientationchange', this.updateViewStyle, false);
  }
}
</script>
<style scoped>
.dropitem.keyboard-hover {
  background-color:rgb(13, 120, 226); 
  outline: none;
}
.separator-line {
  align-self: normal;
  border-right: 1px solid var(--hr-color);
  width: 1px;
  background: none;
  box-shadow: inset 1px 1px 1px 1px #00000045;
  display: inline-flex;
  margin: 3px 7px 3px 0;
}

.dropdown-input-placeholder {
  color: var(--placeholder-color);
}

.dropdown-arrow-icon {
  background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjUiPjxwYXRoIGZpbGw9IiM5OTkiIGZpbGwtcnVsZT0iZXZlbm9kZCIgZD0iTTggLjdMNy4zIDAgNCAzLjMuNyAwIDAgLjdsNCA0IDQtNHoiLz48L3N2Zz4=) no-repeat scroll calc(100% - 5px) center / 1em var(--textColor);
  width: 1.5em;
  min-width: 1.5em;
  background-color: transparent;
  position: relative;
  margin-left: auto;
  cursor: pointer;
  /* height: 1em; */
  min-height: 1em;
  /* margin-top: 6px; */
  /* align-self: normal; */
  background-position-x: right;
}

.list-header {
  background-color: var(--dropdown-container-background);
  color: var(--dropdown-container-text-color);
  max-width: calc(100% - 2em);
  border-radius: 15px;
  border: 1px solid transparent;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0;
}
.list-footer {
  background-color: var(--dropdown-container-background);
  color: var(--dropdown-container-text-color);
  max-width: calc(100% - 2em);
  border-radius: 15px;
  border: 1px solid transparent;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  height: 2em;
  text-align: center;
  align-content: center;
}
.list-header:focus-within {
  border-color: var(--input-border-color-focused);
}
.search-input {
  background: transparent;
  border: none;
  color: white;
  padding: 5px 0;
  padding-left: 15px;
  border: none;
  width: calc(100% - 2rem - 15px);
  font-size: 1rem;
  outline: none;
}
.search-input:focus {
  border: none;
}

.list-header .search-icon {
  background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAsMCwyNTYsMjU2Ij4KPGcgZmlsbD0iI2RkZGRkZCIgZmlsbC1ydWxlPSJub256ZXJvIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgc3Ryb2tlLWxpbmVjYXA9ImJ1dHQiIHN0cm9rZS1saW5lam9pbj0ibWl0ZXIiIHN0cm9rZS1taXRlcmxpbWl0PSIxMCIgc3Ryb2tlLWRhc2hhcnJheT0iIiBzdHJva2UtZGFzaG9mZnNldD0iMCIgZm9udC1mYW1pbHk9Im5vbmUiIGZvbnQtd2VpZ2h0PSJub25lIiBmb250LXNpemU9Im5vbmUiIHRleHQtYW5jaG9yPSJub25lIiBzdHlsZT0ibWl4LWJsZW5kLW1vZGU6IG5vcm1hbCI+PGcgdHJhbnNmb3JtPSJzY2FsZSg1LjEyLDUuMTIpIj48cGF0aCBkPSJNMjEsM2MtOS4zOTg0NCwwIC0xNyw3LjYwMTU2IC0xNywxN2MwLDkuMzk4NDQgNy42MDE1NiwxNyAxNywxN2MzLjM1NTQ3LDAgNi40NjA5NCwtMC45ODQzNyA5LjA5Mzc1LC0yLjY1NjI1bDEyLjI4MTI1LDEyLjI4MTI1bDQuMjUsLTQuMjVsLTEyLjEyNSwtMTIuMDkzNzVjMi4xNzk2OSwtMi44NTkzNyAzLjUsLTYuNDAyMzQgMy41LC0xMC4yODEyNWMwLC05LjM5ODQ0IC03LjYwMTU2LC0xNyAtMTcsLTE3ek0yMSw3YzcuMTk5MjIsMCAxMyw1LjgwMDc4IDEzLDEzYzAsNy4xOTkyMiAtNS44MDA3OCwxMyAtMTMsMTNjLTcuMTk5MjIsMCAtMTMsLTUuODAwNzggLTEzLC0xM2MwLC03LjE5OTIyIDUuODAwNzgsLTEzIDEzLC0xM3oiPjwvcGF0aD48L2c+PC9nPgo8L3N2Zz4=") no-repeat scroll calc(100% - 5px) center / 1em var(--textColor);
  width: 2rem;
  min-width: 2rem;
  background-color: transparent;
  cursor: pointer;
  height: 30px;
  min-height: 1rem;
  display: inline-block;
  position: absolute;
  /* right: 1rem; */
  background-position-x: center;
  background-position-y: center;
}

.dropdown-box {
  display: flex;
  font-size: inherit;
  padding: 0 10px;
  width: calc(100% - 22px);
  max-width: calc(100% - 22px);
  background: var(--input-box-background);
  border-radius: var(--ui-control-border-radius);
  border: 1px solid var(--border-color);
  margin-bottom: 1rem;
}

.dropdown-box.inline {
  padding: 0;
  border: none;
  background: none;
  width: 100%;
  max-width: 100%;
  margin-bottom: 5px;
}

.dropdown-box.auto-size,
.dropdown-box.auto-size #dropdown-input {
  width: auto;
  max-width: fit-content;
}

.dropdownbox-container {
  background-color: rgba(0, 0, 0, 90%);
  backdrop-filter: blur(5px);
  position: fixed;
  top: 0;
  left: 0;
  max-width: 100vw;
  min-width: 5em;
  max-height: 100svh;
  max-height: calc(var(--vh, 1vh) * 100);
  padding: 0;
  padding-top: 1em;
  width: 100vw;
  height: 100vh;
  z-index: 999;
  align-content: center;
}
.dropdownbox-container.has-search-input {
  align-content: start;
}
#dropdown-input .prefix {
  /* white-space: break-spaces; */
  text-align: right;
  display: inline-flex;
  /* padding: 3px 0; */
  /* margin-right: 7px; */
  margin: 3px 7px 3px 0;
}
.dropdown-value {
  margin: 3px 0;
}
#dropdown-input {
  border-radius: 0;
  border: none;
  /* height: 100%; */
  width: 100%;
  max-width: 100%;
  /* border: 1px solid transparent; */
  background: transparent;
  outline: none;
  cursor: default;
  font-size: inherit;
  display: flex;
  flex-flow: row;
  align-items: center;
}
#dropdown-input.disabled {
  color: var(--textColorDisabled);
}

.dropitem { 
  display: flex;
  border-bottom: 1px solid #383D50;
  padding: 0 0 7px 0;
  margin: 10px 10px;
  width: calc(100% - 20px);
  cursor: default;
  font-size: 110%;
}
.dropitem.disabled {
  border-bottom: none;
  color: #626878;
}
.dropitem:last-of-type {
  border-bottom: none;
}
.dropitem:hover {
  background-color: initial;
}
.dropitem.active {
  background-color: initial;
}
.droptext {
  width: 100%;
  height: 100%;
  font-family: system-ui;
  /* display: -webkit-flex; */
  /* display: flex; */
  align-items: center;
  align-self: center;
  display: block;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
}
.drop-radio {
  align-self: center;
}

/* стиль отображения disabled + checked */
input[disabled][type="radio"]:checked {
  position: relative;
  box-sizing: border-box;
  /* height: 1rem; */
  /* width: 1rem; */
  /* margin: 0; */
}
input[disabled][type="radio"]:checked::after {
  position: absolute;
  content: '';
  display: block;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  border-radius: 50%;
  background-color:dodgerblue; 
  border: 2px solid var(--textColorDisabled);
}

.subline-text {
  font-size: 80%;
  color: var(--subline-text-color);
}

.dropitem.active .subline-text {
  color: var(--subline-selected-text-color);
}

.multiselect-ul {
  background-color: var(--dropdown-container-background);
  color: var(--dropdown-container-text-color);
  height: auto;
  min-height: 2em;
  max-height: calc(100% - 7em);
  max-width: calc(100% - 2em);
  border-radius: 15px;
  border: 1px solid transparent;
  overflow-x: hidden;
  overflow-y: auto;
  margin: auto;
  padding: 0;
  outline: none;
  position: relative;
  top: 5px;
}
.multiselect-ul::-webkit-scrollbar {
  background: transparent;
}
.multiselect-ul.hidden-scroll {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.multiselect-ulhidden-scroll::-webkit-scrollbar-thumb,
.multiselect-ul.hidden-scroll::-webkit-scrollbar {
  background: transparent;
  display: none;
}

@media only screen and (min-width: 800px) {
  .multiselect-ul.has-search-input {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    top: 0;
  }
  .multiselect-ul {
    background-color: var(--dropdown-container-background);
    border: 1px solid #949494;
    height: auto;
    border-radius: 3px;
    max-width: 100%;
    margin: 0;
    top: 5px;
    /* border-top: none; */
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.11), 0 2px 2px rgba(0, 0, 0, 0.11), 0 4px 4px rgba(0, 0, 0, 0.11), 0 8px 8px rgba(0, 0, 0, 0.11), 0 16px 16px rgba(0, 0, 0, 0.11), 0 32px 32px rgba(0, 0, 0, 0.11);
  }
  .dropdownbox-container {
    position: fixed;
    background-color: transparent;
    max-width: calc(100svw - 4em);
    max-height: calc(100svh - 8em);
    max-height: calc(var(--vh, 1vh) * 100 - 8em);
    padding: 0;
    width: 15em;
    height: 20em;
    top: 1.5em;
    bottom: auto;
    left: auto;
    right: auto;
    align-content: inherit;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  #dropdown-input {
    padding: 0;
  }
  .list-header {
    max-width: 100%;
    margin-top: 5px;
    background-color: var(--dropdown-container-background);
    color: var(--dropdown-container-text-color);
    border-color: #949494;
    border-radius: 3px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .list-footer {
    display: none;
  }
  .dropitem {
    border-bottom: 1px solid #6b6b6b;
    padding: 4px 10px;
    margin: 0;
    width: calc(100% - 20px);
    font-size: 100%;
  }
  .dropitem:hover {
    /* background-color: #49525c; */
    background-color: #0056b8;
    color: white;
  }
  .dropitem.active {
    background-color: dodgerblue;
    color: white;
  }
  .droptext {
    font-family: inherit;
    font-weight: normal;
  }
  .dropdown-input {
    border-bottom: 1px solid var(--input-border-color);
    padding: 0.5rem 3px;
  }
}
</style>